import { Device } from '../../model';
import type { FilterKey } from './filters-config';
import { compareVersions, isOutdated, parseVersion } from './version';

// Client-side search/filter/sort helpers, applied over the full device set.

export const matchesSearch = (device: Device, search: string): boolean => {
  const query = search.trim().toLowerCase();

  if (query.length === 0) {
    return true;
  }

  return (
    device.organization.toLowerCase().includes(query) ||
    device.location.toLowerCase().includes(query) ||
    device.cid.toString().includes(query)
  );
};

// Matches the backend "low speed" rule: a configured threshold meets/exceeds the measured speed.
export const hasSpeedAlert = (device: Device): boolean =>
  device.transferConfig.badDown >= device.transfer.down ||
  device.transferConfig.badUp >= device.transfer.up;

export const needsUpdate = (device: Device, latestLcs?: string, latestEms?: string): boolean =>
  isOutdated(device.lcs, latestLcs) || isOutdated(device.ems, latestEms);

export const matchesCategory = (
  device: Device,
  key: FilterKey,
  latestLcs?: string,
  latestEms?: string
): boolean => {
  switch (key) {
    case 'online':
      return device.status === Device.Status.online;
    case 'offline':
      return device.status === Device.Status.offline;
    case 'speed':
      return hasSpeedAlert(device);
    case 'updates':
      return needsUpdate(device, latestLcs, latestEms);
    default:
      return true;
  }
};

export const sortDevices = (devices: Device[], sortField: string): Device[] => {
  if (sortField === '') {
    return devices;
  }

  return [...devices].sort((a, b) => {
    switch (sortField) {
      case 'client_id':
        return a.cid - b.cid;
      case 'lcs_version':
        return compareVersions(parseVersion(a.lcs) ?? [], parseVersion(b.lcs) ?? []);
      case 'ems_version':
        return compareVersions(parseVersion(a.ems) ?? [], parseVersion(b.ems) ?? []);
      default:
        return 0;
    }
  });
};
